<template>
    <div class="segment-conditions">
        <div class="segment-form">
            <label class="font-4">Segment Name</label>
            <div>
                <ErrorField :errors="segmentNameErrors">
                    <input
                        id="segmentNameInput"
                        ref="segmentNameInput"
                        type="text"
                        class="segment-name-input"
                        placeholder="Enter the name of the segment rule"
                        :value="name"
                        @input="$emit('update:name', $event.target.value)"
                    />
                </ErrorField>
            </div>
        </div>
        <hr>
        <ul class="segment-list">
            <li v-for="(item, index) in conditionList" :key="index">
                <SegmentCondition
                    :isFirst="index === 0"
                    :id="index"
                    :metadata="metadata"
                    :nameId="item.nameId"
                    :comparator="item.comparator"
                    :value="item.value"
                    :customType="item.type"
                    :customName="item.customName"
                    :allowCustomSegment="conditionList.length === 1"
                    :allowCustomProperties="hasLessThanMaxCustomProperties"
                    :countriesData="countriesData"
                    :errors="errors && errors.conditions ? errors.conditions[index] : {}"
                    :isAndroid="isAndroid"
                    @update:nameId="updateNameId(index, $event)"
                    @update:comparator="updateComparator(index, $event)"
                    @update:value="updateValue(index, $event)"
                    @update:customType="updateCustomType(index, $event)"
                    @update:customName="updateCustomName(index, $event)"
                />
                <span class="remove-btn" v-if="conditionList.length > 1" @click="removeItem(index)">
                    <IconBase iconName="closeIcon" :iconColor="consts.COLORS.DARK_300" width="10" height="10" />
                </span>
            </li>
        </ul>
        <footer>
            <button
                id="addSegmentConditionButton"
                v-if="!customSegmentSelected && conditionList.length < maxConditionsCount"
                class="add-btn font-4 ssa-button button-linkStyle"
                @click="addItem">
                + Condition
            </button>
            <p v-if="customSegmentSelected" class="font-5">
                Selecting a custom segment requires the passing of the user segment details in the API.
                <a :href="links.segments" target="_blank">Read more</a>
            </p>
        </footer>
    </div>
</template>

<script>
import SegmentCondition from '@/pages/monetize/segments/segmentForm/SegmentCondition.vue';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import links from 'services/metaData/links';
import ErrorField from 'components/errorField/ErrorField.vue';
import errorFieldTypes from 'components/errorField/errorFieldTypes';
import { FIELD_TYPES, PROPERTY_IDS, segmentNameMaxLength } from './consts';

export default {
    name: 'SegmentConditions',
    components: { ErrorField, IconBase, SegmentCondition },
    props: {
        conditions: {
            type: Array,
            default: () => ([])
        },
        metadata: {
            type: Object,
            required: true,
            validator: (val) => val.defaultProperties && val.customProperties
        },
        maxConditionsCount: {
            type: Number,
            default: 10
        },
        countriesData: Object,
        isAndroid: Boolean,
        newSegment: Boolean,
        name: {
            type: String,
            required: true
        },
        errors: [Object, Boolean]
    },
    data() {
        return {
            conditionList: this.conditions.length ? this.conditions : [{}]
        };
    },
    mounted() {
        if (this.newSegment) {
            this.$refs.segmentNameInput.focus();
        }
    },
    methods: {
        addItem() {
            this.conditionList.push({});
            this.notifyListChange();
        },
        removeItem(index) {
            this.conditionList.splice(index, 1);
            this.notifyListChange();
        },
        updateNameId(index, value) {
            const updated = { nameId: value };
            const booleanNameIds = this.metadata.defaultProperties
                .filter((prop) => prop.type === FIELD_TYPES.Boolean)
                .map((prop) => prop.nameId);
            if (booleanNameIds.includes(value)) {
                updated.comparator = 'Equals';
            }

            this.conditionList.splice(index, 1, updated);
            this.notifyListChange(index, 'nameId');
        },
        updateComparator(index, value) {
            const item = this.conditionList[index];
            this.$set(item, 'comparator', value);
            this.notifyListChange(index, 'comparator');
        },
        updateValue(index, value) {
            const item = this.conditionList[index];
            this.$set(item, 'value', value);
            this.notifyListChange(index, 'value');
        },
        updateCustomType(index, value) {
            const item = this.conditionList[index];
            this.$set(item, 'type', value);
            this.$delete(item, 'value');
            this.$delete(item, 'comparator');
            this.notifyListChange(index, 'type');
        },
        updateCustomName(index, value) {
            const item = this.conditionList[index];
            this.$set(item, 'customName', value);
            this.notifyListChange(index, 'customName');
        },
        notifyListChange(index, key) {
            this.$emit('update:conditions', this.conditionList);

            if (key) {
                this.$emit('touched', { index, key });
            }
        }
    },
    computed: {
        customSegmentSelected() {
            return this.conditionList.length && this.conditionList[0].nameId === PROPERTY_IDS.customSegment;
        },
        hasLessThanMaxCustomProperties() {
            const maxCustomProperties = 5;
            const countOfCustomProperties = this.conditionList
                .filter((condition) => condition.nameId === PROPERTY_IDS.customProperty).length;

            return countOfCustomProperties < maxCustomProperties;
        },
        segmentNameErrors() {
            const field = 'Segment Name';

            return [
                {
                    type: errorFieldTypes.required,
                    field,
                    condition: this.errors.nameRequired
                },
                {
                    type: errorFieldTypes.maxLength,
                    field,
                    value: segmentNameMaxLength,
                    condition: this.errors.nameMaxLength
                }
            ];
        },
        consts: () => consts,
        links: () => links
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";
@import "~styles/buttons.scss";
@import "~styles/input.scss";

.segment-conditions {
    min-height: 150px;
    background-color: $light-100;
    border: $border;
    padding: 30px 30px 27px;
    margin-bottom: 30px;
}

.segment-form {
    display: grid;
    grid-template-columns: 2fr 5fr 5fr;
    margin-bottom: 20px;

    & > label {
        line-height: 32px;
    }
}

.segment-list {
    margin: 0;
    padding: 0;
    position: relative;

    > li {
        display: flex;
        margin-top: 15px;
    }

    .remove-btn {
        padding: 5px;
        cursor: pointer;
        position: absolute;
        right: -16px;
    }
}

footer {
    margin-left: 78px;
    p {
        margin-top: 11px;
        margin-bottom: 0;
    }
}

hr {
    margin-bottom: 15px;
    border-width: 0;
    border-bottom: $border;
}

.segment-name-input {
    width: 100%;
}
</style>
